import {
  GetTGAHeader,
  init_tga
} from "./chunk-JHLLGXKA.mjs";
import {
  init_abstractEngine_texture
} from "./chunk-FPDBPA6H.mjs";
import {
  init_engine_readTexture
} from "./chunk-YVFJFPPS.mjs";
import {
  PerfCounter,
  init_perfCounter
} from "./chunk-AUDUKNGA.mjs";
import {
  ThinEngine,
  WebGLDataBuffer,
  WebGLHardwareTexture,
  init_thinEngine,
  init_webGLDataBuffer,
  init_webGLHardwareTexture
} from "./chunk-LZKRXJNB.mjs";
import {
  AbstractEngine,
  InternalTexture,
  IsDocumentAvailable,
  IsWindowObjectExist,
  PrecisionDate,
  Tools,
  WebRequest,
  init_abstractEngine,
  init_domManagement,
  init_internalTexture,
  init_precisionDate,
  init_tools,
  init_webRequest
} from "./chunk-YVKSXECI.mjs";
import {
  Logger,
  init_logger
} from "./chunk-CGQJYOWJ.mjs";
import {
  EngineStore,
  Observable,
  init_engineStore,
  init_observable
} from "./chunk-PMCXIDBB.mjs";
import {
  __esm
} from "./chunk-AU4RG7EN.mjs";

// node_modules/@babylonjs/core/Misc/performanceMonitor.js
var PerformanceMonitor, RollingAverage;
var init_performanceMonitor = __esm({
  "node_modules/@babylonjs/core/Misc/performanceMonitor.js"() {
    "use strict";
    init_precisionDate();
    PerformanceMonitor = class {
      /**
       * constructor
       * @param frameSampleSize The number of samples required to saturate the sliding window
       */
      constructor(frameSampleSize = 30) {
        this._enabled = true;
        this._rollingFrameTime = new RollingAverage(frameSampleSize);
      }
      /**
       * Samples current frame
       * @param timeMs A timestamp in milliseconds of the current frame to compare with other frames
       */
      sampleFrame(timeMs = PrecisionDate.Now) {
        if (!this._enabled) {
          return;
        }
        if (this._lastFrameTimeMs != null) {
          const dt = timeMs - this._lastFrameTimeMs;
          this._rollingFrameTime.add(dt);
        }
        this._lastFrameTimeMs = timeMs;
      }
      /**
       * Returns the average frame time in milliseconds over the sliding window (or the subset of frames sampled so far)
       */
      get averageFrameTime() {
        return this._rollingFrameTime.average;
      }
      /**
       * Returns the variance frame time in milliseconds over the sliding window (or the subset of frames sampled so far)
       */
      get averageFrameTimeVariance() {
        return this._rollingFrameTime.variance;
      }
      /**
       * Returns the frame time of the most recent frame
       */
      get instantaneousFrameTime() {
        return this._rollingFrameTime.history(0);
      }
      /**
       * Returns the average framerate in frames per second over the sliding window (or the subset of frames sampled so far)
       */
      get averageFPS() {
        return 1e3 / this._rollingFrameTime.average;
      }
      /**
       * Returns the average framerate in frames per second using the most recent frame time
       */
      get instantaneousFPS() {
        const history = this._rollingFrameTime.history(0);
        if (history === 0) {
          return 0;
        }
        return 1e3 / history;
      }
      /**
       * Returns true if enough samples have been taken to completely fill the sliding window
       */
      get isSaturated() {
        return this._rollingFrameTime.isSaturated();
      }
      /**
       * Enables contributions to the sliding window sample set
       */
      enable() {
        this._enabled = true;
      }
      /**
       * Disables contributions to the sliding window sample set
       * Samples will not be interpolated over the disabled period
       */
      disable() {
        this._enabled = false;
        this._lastFrameTimeMs = null;
      }
      /**
       * Returns true if sampling is enabled
       */
      get isEnabled() {
        return this._enabled;
      }
      /**
       * Resets performance monitor
       */
      reset() {
        this._lastFrameTimeMs = null;
        this._rollingFrameTime.reset();
      }
    };
    RollingAverage = class {
      /**
       * constructor
       * @param length The number of samples required to saturate the sliding window
       */
      constructor(length) {
        this._samples = new Array(length);
        this.reset();
      }
      /**
       * Adds a sample to the sample set
       * @param v The sample value
       */
      add(v) {
        let delta;
        if (this.isSaturated()) {
          const bottomValue = this._samples[this._pos];
          delta = bottomValue - this.average;
          this.average -= delta / (this._sampleCount - 1);
          this._m2 -= delta * (bottomValue - this.average);
        } else {
          this._sampleCount++;
        }
        delta = v - this.average;
        this.average += delta / this._sampleCount;
        this._m2 += delta * (v - this.average);
        this.variance = this._m2 / (this._sampleCount - 1);
        this._samples[this._pos] = v;
        this._pos++;
        this._pos %= this._samples.length;
      }
      /**
       * Returns previously added values or null if outside of history or outside the sliding window domain
       * @param i Index in history. For example, pass 0 for the most recent value and 1 for the value before that
       * @returns Value previously recorded with add() or null if outside of range
       */
      history(i) {
        if (i >= this._sampleCount || i >= this._samples.length) {
          return 0;
        }
        const i0 = this._wrapPosition(this._pos - 1);
        return this._samples[this._wrapPosition(i0 - i)];
      }
      /**
       * Returns true if enough samples have been taken to completely fill the sliding window
       * @returns true if sample-set saturated
       */
      isSaturated() {
        return this._sampleCount >= this._samples.length;
      }
      /**
       * Resets the rolling average (equivalent to 0 samples taken so far)
       */
      reset() {
        this.average = 0;
        this.variance = 0;
        this._sampleCount = 0;
        this._pos = 0;
        this._m2 = 0;
      }
      /**
       * Wraps a value around the sample range boundaries
       * @param i Position in sample range, for example if the sample length is 5, and i is -3, then 2 will be returned.
       * @returns Wrapped position in sample range
       */
      _wrapPosition(i) {
        const max = this._samples.length;
        return (i % max + max) % max;
      }
    };
  }
});

// node_modules/@babylonjs/core/Engines/Extensions/engine.alpha.js
var init_engine_alpha = __esm({
  "node_modules/@babylonjs/core/Engines/Extensions/engine.alpha.js"() {
    "use strict";
    init_thinEngine();
    ThinEngine.prototype.setAlphaMode = function(mode, noDepthWriteChange = false) {
      if (this._alphaMode === mode) {
        if (!noDepthWriteChange) {
          const depthMask = mode === 0;
          if (this.depthCullingState.depthMask !== depthMask) {
            this.depthCullingState.depthMask = depthMask;
          }
        }
        return;
      }
      switch (mode) {
        case 0:
          this._alphaState.alphaBlend = false;
          break;
        case 7:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE, this._gl.ONE_MINUS_SRC_ALPHA, this._gl.ONE, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 8:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE, this._gl.ONE_MINUS_SRC_ALPHA, this._gl.ONE, this._gl.ONE_MINUS_SRC_ALPHA);
          this._alphaState.alphaBlend = true;
          break;
        case 2:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.SRC_ALPHA, this._gl.ONE_MINUS_SRC_ALPHA, this._gl.ONE, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 6:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE, this._gl.ONE, this._gl.ZERO, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 1:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.SRC_ALPHA, this._gl.ONE, this._gl.ZERO, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 3:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ZERO, this._gl.ONE_MINUS_SRC_COLOR, this._gl.ONE, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 4:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.DST_COLOR, this._gl.ZERO, this._gl.ONE, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 5:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.SRC_ALPHA, this._gl.ONE_MINUS_SRC_COLOR, this._gl.ONE, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 9:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.CONSTANT_COLOR, this._gl.ONE_MINUS_CONSTANT_COLOR, this._gl.CONSTANT_ALPHA, this._gl.ONE_MINUS_CONSTANT_ALPHA);
          this._alphaState.alphaBlend = true;
          break;
        case 10:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE, this._gl.ONE_MINUS_SRC_COLOR, this._gl.ONE, this._gl.ONE_MINUS_SRC_ALPHA);
          this._alphaState.alphaBlend = true;
          break;
        case 11:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE, this._gl.ONE, this._gl.ONE, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 12:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.DST_ALPHA, this._gl.ONE, this._gl.ZERO, this._gl.ZERO);
          this._alphaState.alphaBlend = true;
          break;
        case 13:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE_MINUS_DST_COLOR, this._gl.ONE_MINUS_SRC_COLOR, this._gl.ONE_MINUS_DST_ALPHA, this._gl.ONE_MINUS_SRC_ALPHA);
          this._alphaState.alphaBlend = true;
          break;
        case 14:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE, this._gl.ONE_MINUS_SRC_ALPHA, this._gl.ONE, this._gl.ONE_MINUS_SRC_ALPHA);
          this._alphaState.alphaBlend = true;
          break;
        case 15:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE, this._gl.ONE, this._gl.ONE, this._gl.ZERO);
          this._alphaState.alphaBlend = true;
          break;
        case 16:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.ONE_MINUS_DST_COLOR, this._gl.ONE_MINUS_SRC_COLOR, this._gl.ZERO, this._gl.ONE);
          this._alphaState.alphaBlend = true;
          break;
        case 17:
          this._alphaState.setAlphaBlendFunctionParameters(this._gl.SRC_ALPHA, this._gl.ONE_MINUS_SRC_ALPHA, this._gl.ONE, this._gl.ONE_MINUS_SRC_ALPHA);
          this._alphaState.alphaBlend = true;
          break;
      }
      if (!noDepthWriteChange) {
        this.depthCullingState.depthMask = mode === 0;
      }
      this._alphaMode = mode;
    };
  }
});

// node_modules/@babylonjs/core/Engines/Extensions/engine.dynamicBuffer.js
var init_engine_dynamicBuffer = __esm({
  "node_modules/@babylonjs/core/Engines/Extensions/engine.dynamicBuffer.js"() {
    "use strict";
    init_thinEngine();
    ThinEngine.prototype.updateDynamicIndexBuffer = function(indexBuffer, indices, offset = 0) {
      this._currentBoundBuffer[this._gl.ELEMENT_ARRAY_BUFFER] = null;
      this.bindIndexBuffer(indexBuffer);
      let view;
      if (indexBuffer.is32Bits) {
        view = indices instanceof Uint32Array ? indices : new Uint32Array(indices);
      } else {
        view = indices instanceof Uint16Array ? indices : new Uint16Array(indices);
      }
      this._gl.bufferData(this._gl.ELEMENT_ARRAY_BUFFER, view, this._gl.DYNAMIC_DRAW);
      this._resetIndexBufferBinding();
    };
    ThinEngine.prototype.updateDynamicVertexBuffer = function(vertexBuffer, data, byteOffset, byteLength) {
      this.bindArrayBuffer(vertexBuffer);
      if (byteOffset === void 0) {
        byteOffset = 0;
      }
      const dataLength = data.byteLength || data.length;
      if (byteLength === void 0 || byteLength >= dataLength && byteOffset === 0) {
        if (data instanceof Array) {
          this._gl.bufferSubData(this._gl.ARRAY_BUFFER, byteOffset, new Float32Array(data));
        } else {
          this._gl.bufferSubData(this._gl.ARRAY_BUFFER, byteOffset, data);
        }
      } else {
        if (data instanceof Array) {
          this._gl.bufferSubData(this._gl.ARRAY_BUFFER, 0, new Float32Array(data).subarray(byteOffset, byteOffset + byteLength));
        } else {
          if (data instanceof ArrayBuffer) {
            data = new Uint8Array(data, byteOffset, byteLength);
          } else {
            data = new Uint8Array(data.buffer, data.byteOffset + byteOffset, byteLength);
          }
          this._gl.bufferSubData(this._gl.ARRAY_BUFFER, 0, data);
        }
      }
      this._resetVertexBufferBinding();
    };
  }
});

// node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.loadingScreen.js
var init_abstractEngine_loadingScreen = __esm({
  "node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.loadingScreen.js"() {
    "use strict";
    init_domManagement();
    init_abstractEngine();
    AbstractEngine.prototype.displayLoadingUI = function() {
      if (!IsWindowObjectExist()) {
        return;
      }
      const loadingScreen = this.loadingScreen;
      if (loadingScreen) {
        loadingScreen.displayLoadingUI();
      }
    };
    AbstractEngine.prototype.hideLoadingUI = function() {
      if (!IsWindowObjectExist()) {
        return;
      }
      const loadingScreen = this._loadingScreen;
      if (loadingScreen) {
        loadingScreen.hideLoadingUI();
      }
    };
    Object.defineProperty(AbstractEngine.prototype, "loadingScreen", {
      get: function() {
        if (!this._loadingScreen && this._renderingCanvas) {
          this._loadingScreen = AbstractEngine.DefaultLoadingScreenFactory(this._renderingCanvas);
        }
        return this._loadingScreen;
      },
      set: function(value) {
        this._loadingScreen = value;
      },
      enumerable: true,
      configurable: true
    });
    Object.defineProperty(AbstractEngine.prototype, "loadingUIText", {
      set: function(value) {
        this.loadingScreen.loadingUIText = value;
      },
      enumerable: true,
      configurable: true
    });
    Object.defineProperty(AbstractEngine.prototype, "loadingUIBackgroundColor", {
      set: function(value) {
        this.loadingScreen.loadingUIBackgroundColor = value;
      },
      enumerable: true,
      configurable: true
    });
  }
});

// node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.dom.js
var init_abstractEngine_dom = __esm({
  "node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.dom.js"() {
    "use strict";
    init_abstractEngine();
    AbstractEngine.prototype.getInputElement = function() {
      return this._renderingCanvas;
    };
    AbstractEngine.prototype.getRenderingCanvasClientRect = function() {
      if (!this._renderingCanvas) {
        return null;
      }
      return this._renderingCanvas.getBoundingClientRect();
    };
    AbstractEngine.prototype.getInputElementClientRect = function() {
      if (!this._renderingCanvas) {
        return null;
      }
      return this.getInputElement().getBoundingClientRect();
    };
    AbstractEngine.prototype.getAspectRatio = function(viewportOwner, useScreen = false) {
      const viewport = viewportOwner.viewport;
      return this.getRenderWidth(useScreen) * viewport.width / (this.getRenderHeight(useScreen) * viewport.height);
    };
    AbstractEngine.prototype.getScreenAspectRatio = function() {
      return this.getRenderWidth(true) / this.getRenderHeight(true);
    };
    AbstractEngine.prototype._verifyPointerLock = function() {
      var _a;
      (_a = this._onPointerLockChange) == null ? void 0 : _a.call(this);
    };
  }
});

// node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.alpha.js
var init_abstractEngine_alpha = __esm({
  "node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.alpha.js"() {
    "use strict";
    init_abstractEngine();
    AbstractEngine.prototype.setAlphaEquation = function(equation) {
      if (this._alphaEquation === equation) {
        return;
      }
      switch (equation) {
        case 0:
          this._alphaState.setAlphaEquationParameters(32774, 32774);
          break;
        case 1:
          this._alphaState.setAlphaEquationParameters(32778, 32778);
          break;
        case 2:
          this._alphaState.setAlphaEquationParameters(32779, 32779);
          break;
        case 3:
          this._alphaState.setAlphaEquationParameters(32776, 32776);
          break;
        case 4:
          this._alphaState.setAlphaEquationParameters(32775, 32775);
          break;
        case 5:
          this._alphaState.setAlphaEquationParameters(32775, 32774);
          break;
      }
      this._alphaEquation = equation;
    };
  }
});

// node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.states.js
var init_abstractEngine_states = __esm({
  "node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.states.js"() {
    "use strict";
    init_abstractEngine();
    init_abstractEngine_alpha();
    AbstractEngine.prototype.getInputElement = function() {
      return this._renderingCanvas;
    };
    AbstractEngine.prototype.getDepthFunction = function() {
      return this._depthCullingState.depthFunc;
    };
    AbstractEngine.prototype.setDepthFunction = function(depthFunc) {
      this._depthCullingState.depthFunc = depthFunc;
    };
    AbstractEngine.prototype.setDepthFunctionToGreater = function() {
      this.setDepthFunction(516);
    };
    AbstractEngine.prototype.setDepthFunctionToGreaterOrEqual = function() {
      this.setDepthFunction(518);
    };
    AbstractEngine.prototype.setDepthFunctionToLess = function() {
      this.setDepthFunction(513);
    };
    AbstractEngine.prototype.setDepthFunctionToLessOrEqual = function() {
      this.setDepthFunction(515);
    };
    AbstractEngine.prototype.getDepthWrite = function() {
      return this._depthCullingState.depthMask;
    };
    AbstractEngine.prototype.setDepthWrite = function(enable) {
      this._depthCullingState.depthMask = enable;
    };
    AbstractEngine.prototype.getStencilBuffer = function() {
      return this._stencilState.stencilTest;
    };
    AbstractEngine.prototype.setStencilBuffer = function(enable) {
      this._stencilState.stencilTest = enable;
    };
    AbstractEngine.prototype.getStencilMask = function() {
      return this._stencilState.stencilMask;
    };
    AbstractEngine.prototype.setStencilMask = function(mask) {
      this._stencilState.stencilMask = mask;
    };
    AbstractEngine.prototype.getStencilFunction = function() {
      return this._stencilState.stencilFunc;
    };
    AbstractEngine.prototype.getStencilFunctionReference = function() {
      return this._stencilState.stencilFuncRef;
    };
    AbstractEngine.prototype.getStencilFunctionMask = function() {
      return this._stencilState.stencilFuncMask;
    };
    AbstractEngine.prototype.setStencilFunction = function(stencilFunc) {
      this._stencilState.stencilFunc = stencilFunc;
    };
    AbstractEngine.prototype.setStencilFunctionReference = function(reference) {
      this._stencilState.stencilFuncRef = reference;
    };
    AbstractEngine.prototype.setStencilFunctionMask = function(mask) {
      this._stencilState.stencilFuncMask = mask;
    };
    AbstractEngine.prototype.getStencilOperationFail = function() {
      return this._stencilState.stencilOpStencilFail;
    };
    AbstractEngine.prototype.getStencilOperationDepthFail = function() {
      return this._stencilState.stencilOpDepthFail;
    };
    AbstractEngine.prototype.getStencilOperationPass = function() {
      return this._stencilState.stencilOpStencilDepthPass;
    };
    AbstractEngine.prototype.setStencilOperationFail = function(operation) {
      this._stencilState.stencilOpStencilFail = operation;
    };
    AbstractEngine.prototype.setStencilOperationDepthFail = function(operation) {
      this._stencilState.stencilOpDepthFail = operation;
    };
    AbstractEngine.prototype.setStencilOperationPass = function(operation) {
      this._stencilState.stencilOpStencilDepthPass = operation;
    };
    AbstractEngine.prototype.cacheStencilState = function() {
      this._cachedStencilBuffer = this.getStencilBuffer();
      this._cachedStencilFunction = this.getStencilFunction();
      this._cachedStencilMask = this.getStencilMask();
      this._cachedStencilOperationPass = this.getStencilOperationPass();
      this._cachedStencilOperationFail = this.getStencilOperationFail();
      this._cachedStencilOperationDepthFail = this.getStencilOperationDepthFail();
      this._cachedStencilReference = this.getStencilFunctionReference();
    };
    AbstractEngine.prototype.restoreStencilState = function() {
      this.setStencilFunction(this._cachedStencilFunction);
      this.setStencilMask(this._cachedStencilMask);
      this.setStencilBuffer(this._cachedStencilBuffer);
      this.setStencilOperationPass(this._cachedStencilOperationPass);
      this.setStencilOperationFail(this._cachedStencilOperationFail);
      this.setStencilOperationDepthFail(this._cachedStencilOperationDepthFail);
      this.setStencilFunctionReference(this._cachedStencilReference);
    };
    AbstractEngine.prototype.setAlphaConstants = function(r, g, b, a) {
      this._alphaState.setAlphaBlendConstants(r, g, b, a);
    };
    AbstractEngine.prototype.getAlphaMode = function() {
      return this._alphaMode;
    };
    AbstractEngine.prototype.getAlphaEquation = function() {
      return this._alphaEquation;
    };
  }
});

// node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.renderPass.js
var init_abstractEngine_renderPass = __esm({
  "node_modules/@babylonjs/core/Engines/AbstractEngine/abstractEngine.renderPass.js"() {
    "use strict";
    init_abstractEngine();
    AbstractEngine.prototype.getRenderPassNames = function() {
      return this._renderPassNames;
    };
    AbstractEngine.prototype.getCurrentRenderPassName = function() {
      return this._renderPassNames[this.currentRenderPassId];
    };
    AbstractEngine.prototype.createRenderPassId = function(name) {
      const id = ++AbstractEngine._RenderPassIdCounter;
      this._renderPassNames[id] = name != null ? name : "NONAME";
      return id;
    };
    AbstractEngine.prototype.releaseRenderPassId = function(id) {
      this._renderPassNames[id] = void 0;
      for (let s = 0; s < this.scenes.length; ++s) {
        const scene = this.scenes[s];
        for (let m = 0; m < scene.meshes.length; ++m) {
          const mesh = scene.meshes[m];
          if (mesh.subMeshes) {
            for (let b = 0; b < mesh.subMeshes.length; ++b) {
              const subMesh = mesh.subMeshes[b];
              subMesh._removeDrawWrapper(id);
            }
          }
        }
      }
    };
  }
});

// node_modules/@babylonjs/core/Engines/engine.common.js
function _DisableTouchAction(canvas) {
  if (!canvas || !canvas.setAttribute) {
    return;
  }
  canvas.setAttribute("touch-action", "none");
  canvas.style.touchAction = "none";
  canvas.style.webkitTapHighlightColor = "transparent";
}
function _CommonInit(commonEngine, canvas, creationOptions) {
  commonEngine._onCanvasFocus = () => {
    commonEngine.onCanvasFocusObservable.notifyObservers(commonEngine);
  };
  commonEngine._onCanvasBlur = () => {
    commonEngine.onCanvasBlurObservable.notifyObservers(commonEngine);
  };
  commonEngine._onCanvasContextMenu = (evt) => {
    if (commonEngine.disableContextMenu) {
      evt.preventDefault();
    }
  };
  canvas.addEventListener("focus", commonEngine._onCanvasFocus);
  canvas.addEventListener("blur", commonEngine._onCanvasBlur);
  canvas.addEventListener("contextmenu", commonEngine._onCanvasContextMenu);
  commonEngine._onBlur = () => {
    if (commonEngine.disablePerformanceMonitorInBackground) {
      commonEngine.performanceMonitor.disable();
    }
    commonEngine._windowIsBackground = true;
  };
  commonEngine._onFocus = () => {
    if (commonEngine.disablePerformanceMonitorInBackground) {
      commonEngine.performanceMonitor.enable();
    }
    commonEngine._windowIsBackground = false;
  };
  commonEngine._onCanvasPointerOut = (ev) => {
    if (document.elementFromPoint(ev.clientX, ev.clientY) !== canvas) {
      commonEngine.onCanvasPointerOutObservable.notifyObservers(ev);
    }
  };
  const hostWindow = commonEngine.getHostWindow();
  if (hostWindow && typeof hostWindow.addEventListener === "function") {
    hostWindow.addEventListener("blur", commonEngine._onBlur);
    hostWindow.addEventListener("focus", commonEngine._onFocus);
  }
  canvas.addEventListener("pointerout", commonEngine._onCanvasPointerOut);
  if (!creationOptions.doNotHandleTouchAction) {
    _DisableTouchAction(canvas);
  }
  if (!AbstractEngine.audioEngine && creationOptions.audioEngine && AbstractEngine.AudioEngineFactory) {
    AbstractEngine.audioEngine = AbstractEngine.AudioEngineFactory(commonEngine.getRenderingCanvas(), commonEngine.getAudioContext(), commonEngine.getAudioDestination());
  }
  if (IsDocumentAvailable()) {
    commonEngine._onFullscreenChange = () => {
      commonEngine.isFullscreen = !!document.fullscreenElement;
      if (commonEngine.isFullscreen && commonEngine._pointerLockRequested && canvas) {
        RequestPointerlock(canvas);
      }
    };
    document.addEventListener("fullscreenchange", commonEngine._onFullscreenChange, false);
    document.addEventListener("webkitfullscreenchange", commonEngine._onFullscreenChange, false);
    commonEngine._onPointerLockChange = () => {
      commonEngine.isPointerLock = document.pointerLockElement === canvas;
    };
    document.addEventListener("pointerlockchange", commonEngine._onPointerLockChange, false);
    document.addEventListener("webkitpointerlockchange", commonEngine._onPointerLockChange, false);
  }
  commonEngine.enableOfflineSupport = AbstractEngine.OfflineProviderFactory !== void 0;
  commonEngine._deterministicLockstep = !!creationOptions.deterministicLockstep;
  commonEngine._lockstepMaxSteps = creationOptions.lockstepMaxSteps || 0;
  commonEngine._timeStep = creationOptions.timeStep || 1 / 60;
}
function _CommonDispose(commonEngine, canvas) {
  if (EngineStore.Instances.length === 1 && AbstractEngine.audioEngine) {
    AbstractEngine.audioEngine.dispose();
    AbstractEngine.audioEngine = null;
  }
  const hostWindow = commonEngine.getHostWindow();
  if (hostWindow && typeof hostWindow.removeEventListener === "function") {
    hostWindow.removeEventListener("blur", commonEngine._onBlur);
    hostWindow.removeEventListener("focus", commonEngine._onFocus);
  }
  if (canvas) {
    canvas.removeEventListener("focus", commonEngine._onCanvasFocus);
    canvas.removeEventListener("blur", commonEngine._onCanvasBlur);
    canvas.removeEventListener("pointerout", commonEngine._onCanvasPointerOut);
    canvas.removeEventListener("contextmenu", commonEngine._onCanvasContextMenu);
  }
  if (IsDocumentAvailable()) {
    document.removeEventListener("fullscreenchange", commonEngine._onFullscreenChange);
    document.removeEventListener("mozfullscreenchange", commonEngine._onFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", commonEngine._onFullscreenChange);
    document.removeEventListener("msfullscreenchange", commonEngine._onFullscreenChange);
    document.removeEventListener("pointerlockchange", commonEngine._onPointerLockChange);
    document.removeEventListener("mspointerlockchange", commonEngine._onPointerLockChange);
    document.removeEventListener("mozpointerlockchange", commonEngine._onPointerLockChange);
    document.removeEventListener("webkitpointerlockchange", commonEngine._onPointerLockChange);
  }
}
function GetFontOffset(font) {
  const text = document.createElement("span");
  text.textContent = "Hg";
  text.style.font = font;
  const block = document.createElement("div");
  block.style.display = "inline-block";
  block.style.width = "1px";
  block.style.height = "0px";
  block.style.verticalAlign = "bottom";
  const div = document.createElement("div");
  div.style.whiteSpace = "nowrap";
  div.appendChild(text);
  div.appendChild(block);
  document.body.appendChild(div);
  let fontAscent = 0;
  let fontHeight = 0;
  try {
    fontHeight = block.getBoundingClientRect().top - text.getBoundingClientRect().top;
    block.style.verticalAlign = "baseline";
    fontAscent = block.getBoundingClientRect().top - text.getBoundingClientRect().top;
  } finally {
    document.body.removeChild(div);
  }
  return { ascent: fontAscent, height: fontHeight, descent: fontHeight - fontAscent };
}
function CreateImageBitmapFromSource(engine, imageSource, options) {
  const promise = new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      image.decode().then(() => {
        engine.createImageBitmap(image, options).then((imageBitmap) => {
          resolve(imageBitmap);
        });
      });
    };
    image.onerror = () => {
      reject(`Error loading image ${image.src}`);
    };
    image.src = imageSource;
  });
  return promise;
}
function ResizeImageBitmap(engine, image, bufferWidth, bufferHeight) {
  const canvas = engine.createCanvas(bufferWidth, bufferHeight);
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to get 2d context for resizeImageBitmap");
  }
  context.drawImage(image, 0, 0);
  const buffer = context.getImageData(0, 0, bufferWidth, bufferHeight).data;
  return buffer;
}
function RequestFullscreen(element) {
  const requestFunction = element.requestFullscreen || element.webkitRequestFullscreen;
  if (!requestFunction) {
    return;
  }
  requestFunction.call(element);
}
function ExitFullscreen() {
  const anyDoc = document;
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (anyDoc.webkitCancelFullScreen) {
    anyDoc.webkitCancelFullScreen();
  }
}
function RequestPointerlock(element) {
  if (element.requestPointerLock) {
    const promise = element.requestPointerLock();
    if (promise instanceof Promise)
      promise.then(() => {
        element.focus();
      }).catch(() => {
      });
    else
      element.focus();
  }
}
function ExitPointerlock() {
  if (document.exitPointerLock) {
    document.exitPointerLock();
  }
}
var init_engine_common = __esm({
  "node_modules/@babylonjs/core/Engines/engine.common.js"() {
    "use strict";
    init_domManagement();
    init_abstractEngine();
    init_engineStore();
  }
});

// node_modules/@babylonjs/core/Audio/audioEngine.js
var AudioEngine;
var init_audioEngine = __esm({
  "node_modules/@babylonjs/core/Audio/audioEngine.js"() {
    "use strict";
    init_observable();
    init_logger();
    init_abstractEngine();
    init_domManagement();
    AbstractEngine.AudioEngineFactory = (hostElement, audioContext, audioDestination) => {
      return new AudioEngine(hostElement, audioContext, audioDestination);
    };
    AudioEngine = class {
      /**
       * Gets the current AudioContext if available.
       */
      get audioContext() {
        if (!this._audioContextInitialized) {
          this._initializeAudioContext();
        }
        return this._audioContext;
      }
      /**
       * Instantiates a new audio engine.
       *
       * There should be only one per page as some browsers restrict the number
       * of audio contexts you can create.
       * @param hostElement defines the host element where to display the mute icon if necessary
       * @param audioContext defines the audio context to be used by the audio engine
       * @param audioDestination defines the audio destination node to be used by audio engine
       */
      constructor(hostElement = null, audioContext = null, audioDestination = null) {
        this._audioContext = null;
        this._audioContextInitialized = false;
        this._muteButton = null;
        this._audioDestination = null;
        this.canUseWebAudio = false;
        this.WarnedWebAudioUnsupported = false;
        this.isMP3supported = false;
        this.isOGGsupported = false;
        this.unlocked = false;
        this.useCustomUnlockedButton = false;
        this.onAudioUnlockedObservable = new Observable();
        this.onAudioLockedObservable = new Observable();
        this._tryToRun = false;
        this._onResize = () => {
          this._moveButtonToTopLeft();
        };
        if (!IsWindowObjectExist()) {
          return;
        }
        if (typeof window.AudioContext !== "undefined") {
          this.canUseWebAudio = true;
        }
        const audioElem = document.createElement("audio");
        this._hostElement = hostElement;
        this._audioContext = audioContext;
        this._audioDestination = audioDestination;
        try {
          if (audioElem && !!audioElem.canPlayType && (audioElem.canPlayType('audio/mpeg; codecs="mp3"').replace(/^no$/, "") || audioElem.canPlayType("audio/mp3").replace(/^no$/, ""))) {
            this.isMP3supported = true;
          }
        } catch (e) {
        }
        try {
          if (audioElem && !!audioElem.canPlayType && audioElem.canPlayType('audio/ogg; codecs="vorbis"').replace(/^no$/, "")) {
            this.isOGGsupported = true;
          }
        } catch (e) {
        }
      }
      /**
       * Flags the audio engine in Locked state.
       * This happens due to new browser policies preventing audio to autoplay.
       */
      lock() {
        this._triggerSuspendedState();
      }
      /**
       * Unlocks the audio engine once a user action has been done on the dom.
       * This is helpful to resume play once browser policies have been satisfied.
       */
      unlock() {
        var _a, _b;
        if (((_a = this._audioContext) == null ? void 0 : _a.state) === "running") {
          this._hideMuteButton();
          if (!this.unlocked) {
            this.unlocked = true;
            this.onAudioUnlockedObservable.notifyObservers(this);
          }
          return;
        }
        if (this._tryToRun) {
          (_b = this._audioContext) == null ? void 0 : _b.suspend().then(() => {
            this._tryToRun = false;
            this._triggerRunningState();
          });
        } else {
          this._triggerRunningState();
        }
      }
      /** @internal */
      _resumeAudioContextOnStateChange() {
        var _a;
        (_a = this._audioContext) == null ? void 0 : _a.addEventListener("statechange", () => {
          var _a2;
          if (this.unlocked && ((_a2 = this._audioContext) == null ? void 0 : _a2.state) !== "running") {
            this._resumeAudioContext();
          }
        }, {
          once: true,
          passive: true,
          signal: AbortSignal.timeout(3e3)
        });
      }
      _resumeAudioContext() {
        var _a;
        if ((_a = this._audioContext) == null ? void 0 : _a.resume) {
          return this._audioContext.resume();
        }
        return Promise.resolve();
      }
      _initializeAudioContext() {
        try {
          if (this.canUseWebAudio) {
            if (!this._audioContext) {
              this._audioContext = new AudioContext();
            }
            this.masterGain = this._audioContext.createGain();
            this.masterGain.gain.value = 1;
            if (!this._audioDestination) {
              this._audioDestination = this._audioContext.destination;
            }
            this.masterGain.connect(this._audioDestination);
            this._audioContextInitialized = true;
            if (this._audioContext.state === "running") {
              this._triggerRunningState();
            }
          }
        } catch (e) {
          this.canUseWebAudio = false;
          Logger.Error("Web Audio: " + e.message);
        }
      }
      _triggerRunningState() {
        if (this._tryToRun) {
          return;
        }
        this._tryToRun = true;
        this._resumeAudioContext().then(() => {
          this._tryToRun = false;
          if (this._muteButton) {
            this._hideMuteButton();
          }
          this.unlocked = true;
          this.onAudioUnlockedObservable.notifyObservers(this);
        }).catch(() => {
          this._tryToRun = false;
          this.unlocked = false;
        });
      }
      _triggerSuspendedState() {
        this.unlocked = false;
        this.onAudioLockedObservable.notifyObservers(this);
        this._displayMuteButton();
      }
      _displayMuteButton() {
        if (this.useCustomUnlockedButton || this._muteButton) {
          return;
        }
        this._muteButton = document.createElement("BUTTON");
        this._muteButton.className = "babylonUnmuteIcon";
        this._muteButton.id = "babylonUnmuteIconBtn";
        this._muteButton.title = "Unmute";
        const imageUrl = !window.SVGSVGElement ? "https://cdn.babylonjs.com/Assets/audio.png" : "data:image/svg+xml;charset=UTF-8,%3Csvg%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2239%22%20height%3D%2232%22%20viewBox%3D%220%200%2039%2032%22%3E%3Cpath%20fill%3D%22white%22%20d%3D%22M9.625%2018.938l-0.031%200.016h-4.953q-0.016%200-0.031-0.016v-12.453q0-0.016%200.031-0.016h4.953q0.031%200%200.031%200.016v12.453zM12.125%207.688l8.719-8.703v27.453l-8.719-8.719-0.016-0.047v-9.938zM23.359%207.875l1.406-1.406%204.219%204.203%204.203-4.203%201.422%201.406-4.219%204.219%204.219%204.203-1.484%201.359-4.141-4.156-4.219%204.219-1.406-1.422%204.219-4.203z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E";
        const css = ".babylonUnmuteIcon { position: absolute; left: 20px; top: 20px; height: 40px; width: 60px; background-color: rgba(51,51,51,0.7); background-image: url(" + imageUrl + ");  background-size: 80%; background-repeat:no-repeat; background-position: center; background-position-y: 4px; border: none; outline: none; transition: transform 0.125s ease-out; cursor: pointer; z-index: 9999; } .babylonUnmuteIcon:hover { transform: scale(1.05) } .babylonUnmuteIcon:active { background-color: rgba(51,51,51,1) }";
        const style = document.createElement("style");
        style.appendChild(document.createTextNode(css));
        document.getElementsByTagName("head")[0].appendChild(style);
        document.body.appendChild(this._muteButton);
        this._moveButtonToTopLeft();
        this._muteButton.addEventListener("touchend", () => {
          this._triggerRunningState();
        }, true);
        this._muteButton.addEventListener("click", () => {
          this.unlock();
        }, true);
        window.addEventListener("resize", this._onResize);
      }
      _moveButtonToTopLeft() {
        if (this._hostElement && this._muteButton) {
          this._muteButton.style.top = this._hostElement.offsetTop + 20 + "px";
          this._muteButton.style.left = this._hostElement.offsetLeft + 20 + "px";
        }
      }
      _hideMuteButton() {
        if (this._muteButton) {
          document.body.removeChild(this._muteButton);
          this._muteButton = null;
        }
      }
      /**
       * Destroy and release the resources associated with the audio context.
       */
      dispose() {
        if (this.canUseWebAudio && this._audioContextInitialized) {
          if (this._connectedAnalyser && this._audioContext) {
            this._connectedAnalyser.stopDebugCanvas();
            this._connectedAnalyser.dispose();
            this.masterGain.disconnect();
            this.masterGain.connect(this._audioContext.destination);
            this._connectedAnalyser = null;
          }
          this.masterGain.gain.value = 1;
        }
        this.WarnedWebAudioUnsupported = false;
        this._hideMuteButton();
        window.removeEventListener("resize", this._onResize);
        this.onAudioUnlockedObservable.clear();
        this.onAudioLockedObservable.clear();
      }
      /**
       * Gets the global volume sets on the master gain.
       * @returns the global volume if set or -1 otherwise
       */
      getGlobalVolume() {
        if (this.canUseWebAudio && this._audioContextInitialized) {
          return this.masterGain.gain.value;
        } else {
          return -1;
        }
      }
      /**
       * Sets the global volume of your experience (sets on the master gain).
       * @param newVolume Defines the new global volume of the application
       */
      setGlobalVolume(newVolume) {
        if (this.canUseWebAudio && this._audioContextInitialized) {
          this.masterGain.gain.value = newVolume;
        }
      }
      /**
       * Connect the audio engine to an audio analyser allowing some amazing
       * synchronization between the sounds/music and your visualization (VuMeter for instance).
       * @see https://doc.babylonjs.com/features/featuresDeepDive/audio/playingSoundsMusic#using-the-analyser
       * @param analyser The analyser to connect to the engine
       */
      connectToAnalyser(analyser) {
        if (this._connectedAnalyser) {
          this._connectedAnalyser.stopDebugCanvas();
        }
        if (this.canUseWebAudio && this._audioContextInitialized && this._audioContext) {
          this._connectedAnalyser = analyser;
          this.masterGain.disconnect();
          this._connectedAnalyser.connectAudioNodes(this.masterGain, this._audioContext.destination);
        }
      }
    };
  }
});

// node_modules/@babylonjs/core/Engines/engine.js
var Engine;
var init_engine = __esm({
  "node_modules/@babylonjs/core/Engines/engine.js"() {
    "use strict";
    init_internalTexture();
    init_engineStore();
    init_thinEngine();
    init_performanceMonitor();
    init_webGLDataBuffer();
    init_logger();
    init_webGLHardwareTexture();
    init_engine_alpha();
    init_engine_readTexture();
    init_engine_dynamicBuffer();
    init_abstractEngine_loadingScreen();
    init_abstractEngine_dom();
    init_abstractEngine_states();
    init_abstractEngine_renderPass();
    init_abstractEngine_texture();
    init_abstractEngine();
    init_engine_common();
    init_perfCounter();
    init_audioEngine();
    Engine = class _Engine extends ThinEngine {
      /**
       * Returns the current npm package of the sdk
       */
      // Not mixed with Version for tooling purpose.
      static get NpmPackage() {
        return AbstractEngine.NpmPackage;
      }
      /**
       * Returns the current version of the framework
       */
      static get Version() {
        return AbstractEngine.Version;
      }
      /** Gets the list of created engines */
      static get Instances() {
        return EngineStore.Instances;
      }
      /**
       * Gets the latest created engine
       */
      static get LastCreatedEngine() {
        return EngineStore.LastCreatedEngine;
      }
      /**
       * Gets the latest created scene
       */
      static get LastCreatedScene() {
        return EngineStore.LastCreatedScene;
      }
      /** @internal */
      // eslint-disable-next-line jsdoc/require-returns-check
      /**
       * Method called to create the default loading screen.
       * This can be overridden in your own app.
       * @param canvas The rendering canvas element
       * @returns The loading screen
       */
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      static DefaultLoadingScreenFactory(canvas) {
        return AbstractEngine.DefaultLoadingScreenFactory(canvas);
      }
      get _supportsHardwareTextureRescaling() {
        return !!_Engine._RescalePostProcessFactory;
      }
      _measureFps() {
        this._performanceMonitor.sampleFrame();
        this._fps = this._performanceMonitor.averageFPS;
        this._deltaTime = this._performanceMonitor.instantaneousFrameTime || 0;
      }
      /**
       * Gets the performance monitor attached to this engine
       * @see https://doc.babylonjs.com/features/featuresDeepDive/scene/optimize_your_scene#engineinstrumentation
       */
      get performanceMonitor() {
        return this._performanceMonitor;
      }
      // Events
      /**
       * Creates a new engine
       * @param canvasOrContext defines the canvas or WebGL context to use for rendering. If you provide a WebGL context, Babylon.js will not hook events on the canvas (like pointers, keyboards, etc...) so no event observables will be available. This is mostly used when Babylon.js is used as a plugin on a system which already used the WebGL context
       * @param antialias defines enable antialiasing (default: false)
       * @param options defines further options to be sent to the getContext() function
       * @param adaptToDeviceRatio defines whether to adapt to the device's viewport characteristics (default: false)
       */
      constructor(canvasOrContext, antialias, options, adaptToDeviceRatio = false) {
        super(canvasOrContext, antialias, options, adaptToDeviceRatio);
        this.customAnimationFrameRequester = null;
        this._performanceMonitor = new PerformanceMonitor();
        this._drawCalls = new PerfCounter();
        if (!canvasOrContext) {
          return;
        }
        this._features.supportRenderPasses = true;
        options = this._creationOptions;
        if (canvasOrContext.getContext) {
          const canvas = canvasOrContext;
          this._sharedInit(canvas);
        }
      }
      _initGLContext() {
        super._initGLContext();
        this._rescalePostProcess = null;
      }
      /**
       * Shared initialization across engines types.
       * @param canvas The canvas associated with this instance of the engine.
       */
      _sharedInit(canvas) {
        super._sharedInit(canvas);
        _CommonInit(this, canvas, this._creationOptions);
      }
      /**
       * Resize an image and returns the image data as an uint8array
       * @param image image to resize
       * @param bufferWidth destination buffer width
       * @param bufferHeight destination buffer height
       * @returns an uint8array containing RGBA values of bufferWidth * bufferHeight size
       */
      resizeImageBitmap(image, bufferWidth, bufferHeight) {
        return ResizeImageBitmap(this, image, bufferWidth, bufferHeight);
      }
      /**
       * Engine abstraction for loading and creating an image bitmap from a given source string.
       * @param imageSource source to load the image from.
       * @param options An object that sets options for the image's extraction.
       * @returns ImageBitmap
       */
      _createImageBitmapFromSource(imageSource, options) {
        return CreateImageBitmapFromSource(this, imageSource, options);
      }
      /**
       * Toggle full screen mode
       * @param requestPointerLock defines if a pointer lock should be requested from the user
       */
      switchFullscreen(requestPointerLock) {
        if (this.isFullscreen) {
          this.exitFullscreen();
        } else {
          this.enterFullscreen(requestPointerLock);
        }
      }
      /**
       * Enters full screen mode
       * @param requestPointerLock defines if a pointer lock should be requested from the user
       */
      enterFullscreen(requestPointerLock) {
        if (!this.isFullscreen) {
          this._pointerLockRequested = requestPointerLock;
          if (this._renderingCanvas) {
            RequestFullscreen(this._renderingCanvas);
          }
        }
      }
      /**
       * Exits full screen mode
       */
      exitFullscreen() {
        if (this.isFullscreen) {
          ExitFullscreen();
        }
      }
      /** States */
      /**
       * Sets a boolean indicating if the dithering state is enabled or disabled
       * @param value defines the dithering state
       */
      setDitheringState(value) {
        if (value) {
          this._gl.enable(this._gl.DITHER);
        } else {
          this._gl.disable(this._gl.DITHER);
        }
      }
      /**
       * Sets a boolean indicating if the rasterizer state is enabled or disabled
       * @param value defines the rasterizer state
       */
      setRasterizerState(value) {
        if (value) {
          this._gl.disable(this._gl.RASTERIZER_DISCARD);
        } else {
          this._gl.enable(this._gl.RASTERIZER_DISCARD);
        }
      }
      /**
       * Directly set the WebGL Viewport
       * @param x defines the x coordinate of the viewport (in screen space)
       * @param y defines the y coordinate of the viewport (in screen space)
       * @param width defines the width of the viewport (in screen space)
       * @param height defines the height of the viewport (in screen space)
       * @returns the current viewport Object (if any) that is being replaced by this call. You can restore this viewport later on to go back to the original state
       */
      setDirectViewport(x, y, width, height) {
        const currentViewport = this._cachedViewport;
        this._cachedViewport = null;
        this._viewport(x, y, width, height);
        return currentViewport;
      }
      /**
       * Executes a scissor clear (ie. a clear on a specific portion of the screen)
       * @param x defines the x-coordinate of the bottom left corner of the clear rectangle
       * @param y defines the y-coordinate of the corner of the clear rectangle
       * @param width defines the width of the clear rectangle
       * @param height defines the height of the clear rectangle
       * @param clearColor defines the clear color
       */
      scissorClear(x, y, width, height, clearColor) {
        this.enableScissor(x, y, width, height);
        this.clear(clearColor, true, true, true);
        this.disableScissor();
      }
      /**
       * Enable scissor test on a specific rectangle (ie. render will only be executed on a specific portion of the screen)
       * @param x defines the x-coordinate of the bottom left corner of the clear rectangle
       * @param y defines the y-coordinate of the corner of the clear rectangle
       * @param width defines the width of the clear rectangle
       * @param height defines the height of the clear rectangle
       */
      enableScissor(x, y, width, height) {
        const gl = this._gl;
        gl.enable(gl.SCISSOR_TEST);
        gl.scissor(x, y, width, height);
      }
      /**
       * Disable previously set scissor test rectangle
       */
      disableScissor() {
        const gl = this._gl;
        gl.disable(gl.SCISSOR_TEST);
      }
      /**
       * @internal
       */
      _loadFileAsync(url, offlineProvider, useArrayBuffer) {
        return new Promise((resolve, reject) => {
          this._loadFile(url, (data) => {
            resolve(data);
          }, void 0, offlineProvider, useArrayBuffer, (request, exception) => {
            reject(exception);
          });
        });
      }
      /**
       * Gets the source code of the vertex shader associated with a specific webGL program
       * @param program defines the program to use
       * @returns a string containing the source code of the vertex shader associated with the program
       */
      getVertexShaderSource(program) {
        const shaders = this._gl.getAttachedShaders(program);
        if (!shaders) {
          return null;
        }
        return this._gl.getShaderSource(shaders[0]);
      }
      /**
       * Gets the source code of the fragment shader associated with a specific webGL program
       * @param program defines the program to use
       * @returns a string containing the source code of the fragment shader associated with the program
       */
      getFragmentShaderSource(program) {
        const shaders = this._gl.getAttachedShaders(program);
        if (!shaders) {
          return null;
        }
        return this._gl.getShaderSource(shaders[1]);
      }
      /**
       * sets the object from which width and height will be taken from when getting render width and height
       * Will fallback to the gl object
       * @param dimensions the framebuffer width and height that will be used.
       */
      set framebufferDimensionsObject(dimensions) {
        this._framebufferDimensionsObject = dimensions;
        if (this._framebufferDimensionsObject) {
          this.onResizeObservable.notifyObservers(this);
        }
      }
      _rebuildBuffers() {
        for (const scene of this.scenes) {
          scene.resetCachedMaterial();
          scene._rebuildGeometries();
        }
        for (const scene of this._virtualScenes) {
          scene.resetCachedMaterial();
          scene._rebuildGeometries();
        }
        super._rebuildBuffers();
      }
      /**
       * Get Font size information
       * @param font font name
       * @returns an object containing ascent, height and descent
       */
      getFontOffset(font) {
        return GetFontOffset(font);
      }
      _cancelFrame() {
        if (this.customAnimationFrameRequester) {
          if (this._frameHandler !== 0) {
            this._frameHandler = 0;
            const { cancelAnimationFrame } = this.customAnimationFrameRequester;
            if (cancelAnimationFrame) {
              cancelAnimationFrame(this.customAnimationFrameRequester.requestID);
            }
          }
        } else {
          super._cancelFrame();
        }
      }
      _renderLoop() {
        this._frameHandler = 0;
        if (!this._contextWasLost) {
          let shouldRender = true;
          if (this.isDisposed || !this.renderEvenInBackground && this._windowIsBackground) {
            shouldRender = false;
          }
          if (shouldRender) {
            this.beginFrame();
            if (!this._renderViews()) {
              this._renderFrame();
            }
            this.endFrame();
          }
        }
        if (this._activeRenderLoops.length > 0 && this._frameHandler === 0) {
          if (this.customAnimationFrameRequester) {
            this.customAnimationFrameRequester.requestID = this._queueNewFrame(this.customAnimationFrameRequester.renderFunction || this._boundRenderFunction, this.customAnimationFrameRequester);
            this._frameHandler = this.customAnimationFrameRequester.requestID;
          } else {
            this._frameHandler = this._queueNewFrame(this._boundRenderFunction, this.getHostWindow());
          }
        }
      }
      /**
       * Enters Pointerlock mode
       */
      enterPointerlock() {
        if (this._renderingCanvas) {
          RequestPointerlock(this._renderingCanvas);
        }
      }
      /**
       * Exits Pointerlock mode
       */
      exitPointerlock() {
        ExitPointerlock();
      }
      /**
       * Begin a new frame
       */
      beginFrame() {
        this._measureFps();
        super.beginFrame();
      }
      _deletePipelineContext(pipelineContext) {
        const webGLPipelineContext = pipelineContext;
        if (webGLPipelineContext && webGLPipelineContext.program) {
          if (webGLPipelineContext.transformFeedback) {
            this.deleteTransformFeedback(webGLPipelineContext.transformFeedback);
            webGLPipelineContext.transformFeedback = null;
          }
        }
        super._deletePipelineContext(pipelineContext);
      }
      createShaderProgram(pipelineContext, vertexCode, fragmentCode, defines, context, transformFeedbackVaryings = null) {
        context = context || this._gl;
        this.onBeforeShaderCompilationObservable.notifyObservers(this);
        const program = super.createShaderProgram(pipelineContext, vertexCode, fragmentCode, defines, context, transformFeedbackVaryings);
        this.onAfterShaderCompilationObservable.notifyObservers(this);
        return program;
      }
      _createShaderProgram(pipelineContext, vertexShader, fragmentShader, context, transformFeedbackVaryings = null) {
        const shaderProgram = context.createProgram();
        pipelineContext.program = shaderProgram;
        if (!shaderProgram) {
          throw new Error("Unable to create program");
        }
        context.attachShader(shaderProgram, vertexShader);
        context.attachShader(shaderProgram, fragmentShader);
        if (this.webGLVersion > 1 && transformFeedbackVaryings) {
          const transformFeedback = this.createTransformFeedback();
          this.bindTransformFeedback(transformFeedback);
          this.setTranformFeedbackVaryings(shaderProgram, transformFeedbackVaryings);
          pipelineContext.transformFeedback = transformFeedback;
        }
        context.linkProgram(shaderProgram);
        if (this.webGLVersion > 1 && transformFeedbackVaryings) {
          this.bindTransformFeedback(null);
        }
        pipelineContext.context = context;
        pipelineContext.vertexShader = vertexShader;
        pipelineContext.fragmentShader = fragmentShader;
        if (!pipelineContext.isParallelCompiled) {
          this._finalizePipelineContext(pipelineContext);
        }
        return shaderProgram;
      }
      /**
       * @internal
       */
      _releaseTexture(texture) {
        super._releaseTexture(texture);
      }
      /**
       * @internal
       */
      _releaseRenderTargetWrapper(rtWrapper) {
        super._releaseRenderTargetWrapper(rtWrapper);
        this.scenes.forEach((scene) => {
          scene.postProcesses.forEach((postProcess) => {
            if (postProcess._outputTexture === rtWrapper) {
              postProcess._outputTexture = null;
            }
          });
          scene.cameras.forEach((camera) => {
            camera._postProcesses.forEach((postProcess) => {
              if (postProcess) {
                if (postProcess._outputTexture === rtWrapper) {
                  postProcess._outputTexture = null;
                }
              }
            });
          });
        });
      }
      /**
       * @internal
       * Rescales a texture
       * @param source input texture
       * @param destination destination texture
       * @param scene scene to use to render the resize
       * @param internalFormat format to use when resizing
       * @param onComplete callback to be called when resize has completed
       */
      _rescaleTexture(source, destination, scene, internalFormat, onComplete) {
        this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_MAG_FILTER, this._gl.LINEAR);
        this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_MIN_FILTER, this._gl.LINEAR);
        this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_WRAP_S, this._gl.CLAMP_TO_EDGE);
        this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_WRAP_T, this._gl.CLAMP_TO_EDGE);
        const rtt = this.createRenderTargetTexture({
          width: destination.width,
          height: destination.height
        }, {
          generateMipMaps: false,
          type: 0,
          samplingMode: 2,
          generateDepthBuffer: false,
          generateStencilBuffer: false
        });
        if (!this._rescalePostProcess && _Engine._RescalePostProcessFactory) {
          this._rescalePostProcess = _Engine._RescalePostProcessFactory(this);
        }
        if (this._rescalePostProcess) {
          this._rescalePostProcess.externalTextureSamplerBinding = true;
          const onCompiled = () => {
            this._rescalePostProcess.onApply = function(effect2) {
              effect2._bindTexture("textureSampler", source);
            };
            let hostingScene = scene;
            if (!hostingScene) {
              hostingScene = this.scenes[this.scenes.length - 1];
            }
            hostingScene.postProcessManager.directRender([this._rescalePostProcess], rtt, true);
            this._bindTextureDirectly(this._gl.TEXTURE_2D, destination, true);
            this._gl.copyTexImage2D(this._gl.TEXTURE_2D, 0, internalFormat, 0, 0, destination.width, destination.height, 0);
            this.unBindFramebuffer(rtt);
            rtt.dispose();
            if (onComplete) {
              onComplete();
            }
          };
          const effect = this._rescalePostProcess.getEffect();
          if (effect) {
            effect.executeWhenCompiled(onCompiled);
          } else {
            this._rescalePostProcess.onEffectCreatedObservable.addOnce((effect2) => {
              effect2.executeWhenCompiled(onCompiled);
            });
          }
        }
      }
      /**
       * Wraps an external web gl texture in a Babylon texture.
       * @param texture defines the external texture
       * @param hasMipMaps defines whether the external texture has mip maps (default: false)
       * @param samplingMode defines the sampling mode for the external texture (default: 3)
       * @param width defines the width for the external texture (default: 0)
       * @param height defines the height for the external texture (default: 0)
       * @returns the babylon internal texture
       */
      wrapWebGLTexture(texture, hasMipMaps = false, samplingMode = 3, width = 0, height = 0) {
        const hardwareTexture = new WebGLHardwareTexture(texture, this._gl);
        const internalTexture = new InternalTexture(this, 0, true);
        internalTexture._hardwareTexture = hardwareTexture;
        internalTexture.baseWidth = width;
        internalTexture.baseHeight = height;
        internalTexture.width = width;
        internalTexture.height = height;
        internalTexture.isReady = true;
        internalTexture.useMipMaps = hasMipMaps;
        this.updateTextureSamplingMode(samplingMode, internalTexture);
        return internalTexture;
      }
      /**
       * @internal
       */
      _uploadImageToTexture(texture, image, faceIndex = 0, lod = 0) {
        const gl = this._gl;
        const textureType = this._getWebGLTextureType(texture.type);
        const format = this._getInternalFormat(texture.format);
        const internalFormat = this._getRGBABufferInternalSizedFormat(texture.type, format);
        const bindTarget = texture.isCube ? gl.TEXTURE_CUBE_MAP : gl.TEXTURE_2D;
        this._bindTextureDirectly(bindTarget, texture, true);
        this._unpackFlipY(texture.invertY);
        let target = gl.TEXTURE_2D;
        if (texture.isCube) {
          target = gl.TEXTURE_CUBE_MAP_POSITIVE_X + faceIndex;
        }
        gl.texImage2D(target, lod, internalFormat, format, textureType, image);
        this._bindTextureDirectly(bindTarget, null, true);
      }
      /**
       * Updates a depth texture Comparison Mode and Function.
       * If the comparison Function is equal to 0, the mode will be set to none.
       * Otherwise, this only works in webgl 2 and requires a shadow sampler in the shader.
       * @param texture The texture to set the comparison function for
       * @param comparisonFunction The comparison function to set, 0 if no comparison required
       */
      updateTextureComparisonFunction(texture, comparisonFunction) {
        if (this.webGLVersion === 1) {
          Logger.Error("WebGL 1 does not support texture comparison.");
          return;
        }
        const gl = this._gl;
        if (texture.isCube) {
          this._bindTextureDirectly(this._gl.TEXTURE_CUBE_MAP, texture, true);
          if (comparisonFunction === 0) {
            gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_COMPARE_FUNC, 515);
            gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_COMPARE_MODE, gl.NONE);
          } else {
            gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_COMPARE_FUNC, comparisonFunction);
            gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
          }
          this._bindTextureDirectly(this._gl.TEXTURE_CUBE_MAP, null);
        } else {
          this._bindTextureDirectly(this._gl.TEXTURE_2D, texture, true);
          if (comparisonFunction === 0) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, 515);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.NONE);
          } else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, comparisonFunction);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
          }
          this._bindTextureDirectly(this._gl.TEXTURE_2D, null);
        }
        texture._comparisonFunction = comparisonFunction;
      }
      /**
       * Creates a webGL buffer to use with instantiation
       * @param capacity defines the size of the buffer
       * @returns the webGL buffer
       */
      createInstancesBuffer(capacity) {
        const buffer = this._gl.createBuffer();
        if (!buffer) {
          throw new Error("Unable to create instance buffer");
        }
        const result = new WebGLDataBuffer(buffer);
        result.capacity = capacity;
        this.bindArrayBuffer(result);
        this._gl.bufferData(this._gl.ARRAY_BUFFER, capacity, this._gl.DYNAMIC_DRAW);
        result.references = 1;
        return result;
      }
      /**
       * Delete a webGL buffer used with instantiation
       * @param buffer defines the webGL buffer to delete
       */
      deleteInstancesBuffer(buffer) {
        this._gl.deleteBuffer(buffer);
      }
      _clientWaitAsync(sync, flags = 0, intervalms = 10) {
        const gl = this._gl;
        return new Promise((resolve, reject) => {
          const check = () => {
            const res = gl.clientWaitSync(sync, flags, 0);
            if (res == gl.WAIT_FAILED) {
              reject();
              return;
            }
            if (res == gl.TIMEOUT_EXPIRED) {
              setTimeout(check, intervalms);
              return;
            }
            resolve();
          };
          check();
        });
      }
      /**
       * @internal
       */
      _readPixelsAsync(x, y, w, h, format, type, outputBuffer) {
        if (this._webGLVersion < 2) {
          throw new Error("_readPixelsAsync only work on WebGL2+");
        }
        const gl = this._gl;
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buf);
        gl.bufferData(gl.PIXEL_PACK_BUFFER, outputBuffer.byteLength, gl.STREAM_READ);
        gl.readPixels(x, y, w, h, format, type, 0);
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
        const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
        if (!sync) {
          return null;
        }
        gl.flush();
        return this._clientWaitAsync(sync, 0, 10).then(() => {
          gl.deleteSync(sync);
          gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buf);
          gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, outputBuffer);
          gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
          gl.deleteBuffer(buf);
          return outputBuffer;
        });
      }
      dispose() {
        this.hideLoadingUI();
        if (this._rescalePostProcess) {
          this._rescalePostProcess.dispose();
        }
        _CommonDispose(this, this._renderingCanvas);
        super.dispose();
      }
    };
    Engine.ALPHA_DISABLE = 0;
    Engine.ALPHA_ADD = 1;
    Engine.ALPHA_COMBINE = 2;
    Engine.ALPHA_SUBTRACT = 3;
    Engine.ALPHA_MULTIPLY = 4;
    Engine.ALPHA_MAXIMIZED = 5;
    Engine.ALPHA_ONEONE = 6;
    Engine.ALPHA_PREMULTIPLIED = 7;
    Engine.ALPHA_PREMULTIPLIED_PORTERDUFF = 8;
    Engine.ALPHA_INTERPOLATE = 9;
    Engine.ALPHA_SCREENMODE = 10;
    Engine.DELAYLOADSTATE_NONE = 0;
    Engine.DELAYLOADSTATE_LOADED = 1;
    Engine.DELAYLOADSTATE_LOADING = 2;
    Engine.DELAYLOADSTATE_NOTLOADED = 4;
    Engine.NEVER = 512;
    Engine.ALWAYS = 519;
    Engine.LESS = 513;
    Engine.EQUAL = 514;
    Engine.LEQUAL = 515;
    Engine.GREATER = 516;
    Engine.GEQUAL = 518;
    Engine.NOTEQUAL = 517;
    Engine.KEEP = 7680;
    Engine.REPLACE = 7681;
    Engine.INCR = 7682;
    Engine.DECR = 7683;
    Engine.INVERT = 5386;
    Engine.INCR_WRAP = 34055;
    Engine.DECR_WRAP = 34056;
    Engine.TEXTURE_CLAMP_ADDRESSMODE = 0;
    Engine.TEXTURE_WRAP_ADDRESSMODE = 1;
    Engine.TEXTURE_MIRROR_ADDRESSMODE = 2;
    Engine.TEXTUREFORMAT_ALPHA = 0;
    Engine.TEXTUREFORMAT_LUMINANCE = 1;
    Engine.TEXTUREFORMAT_LUMINANCE_ALPHA = 2;
    Engine.TEXTUREFORMAT_RGB = 4;
    Engine.TEXTUREFORMAT_RGBA = 5;
    Engine.TEXTUREFORMAT_RED = 6;
    Engine.TEXTUREFORMAT_R = 6;
    Engine.TEXTUREFORMAT_RG = 7;
    Engine.TEXTUREFORMAT_RED_INTEGER = 8;
    Engine.TEXTUREFORMAT_R_INTEGER = 8;
    Engine.TEXTUREFORMAT_RG_INTEGER = 9;
    Engine.TEXTUREFORMAT_RGB_INTEGER = 10;
    Engine.TEXTUREFORMAT_RGBA_INTEGER = 11;
    Engine.TEXTURETYPE_UNSIGNED_BYTE = 0;
    Engine.TEXTURETYPE_UNSIGNED_INT = 0;
    Engine.TEXTURETYPE_FLOAT = 1;
    Engine.TEXTURETYPE_HALF_FLOAT = 2;
    Engine.TEXTURETYPE_BYTE = 3;
    Engine.TEXTURETYPE_SHORT = 4;
    Engine.TEXTURETYPE_UNSIGNED_SHORT = 5;
    Engine.TEXTURETYPE_INT = 6;
    Engine.TEXTURETYPE_UNSIGNED_INTEGER = 7;
    Engine.TEXTURETYPE_UNSIGNED_SHORT_4_4_4_4 = 8;
    Engine.TEXTURETYPE_UNSIGNED_SHORT_5_5_5_1 = 9;
    Engine.TEXTURETYPE_UNSIGNED_SHORT_5_6_5 = 10;
    Engine.TEXTURETYPE_UNSIGNED_INT_2_10_10_10_REV = 11;
    Engine.TEXTURETYPE_UNSIGNED_INT_24_8 = 12;
    Engine.TEXTURETYPE_UNSIGNED_INT_10F_11F_11F_REV = 13;
    Engine.TEXTURETYPE_UNSIGNED_INT_5_9_9_9_REV = 14;
    Engine.TEXTURETYPE_FLOAT_32_UNSIGNED_INT_24_8_REV = 15;
    Engine.TEXTURE_NEAREST_SAMPLINGMODE = 1;
    Engine.TEXTURE_BILINEAR_SAMPLINGMODE = 2;
    Engine.TEXTURE_TRILINEAR_SAMPLINGMODE = 3;
    Engine.TEXTURE_NEAREST_NEAREST_MIPLINEAR = 8;
    Engine.TEXTURE_LINEAR_LINEAR_MIPNEAREST = 11;
    Engine.TEXTURE_LINEAR_LINEAR_MIPLINEAR = 3;
    Engine.TEXTURE_NEAREST_NEAREST_MIPNEAREST = 4;
    Engine.TEXTURE_NEAREST_LINEAR_MIPNEAREST = 5;
    Engine.TEXTURE_NEAREST_LINEAR_MIPLINEAR = 6;
    Engine.TEXTURE_NEAREST_LINEAR = 7;
    Engine.TEXTURE_NEAREST_NEAREST = 1;
    Engine.TEXTURE_LINEAR_NEAREST_MIPNEAREST = 9;
    Engine.TEXTURE_LINEAR_NEAREST_MIPLINEAR = 10;
    Engine.TEXTURE_LINEAR_LINEAR = 2;
    Engine.TEXTURE_LINEAR_NEAREST = 12;
    Engine.TEXTURE_EXPLICIT_MODE = 0;
    Engine.TEXTURE_SPHERICAL_MODE = 1;
    Engine.TEXTURE_PLANAR_MODE = 2;
    Engine.TEXTURE_CUBIC_MODE = 3;
    Engine.TEXTURE_PROJECTION_MODE = 4;
    Engine.TEXTURE_SKYBOX_MODE = 5;
    Engine.TEXTURE_INVCUBIC_MODE = 6;
    Engine.TEXTURE_EQUIRECTANGULAR_MODE = 7;
    Engine.TEXTURE_FIXED_EQUIRECTANGULAR_MODE = 8;
    Engine.TEXTURE_FIXED_EQUIRECTANGULAR_MIRRORED_MODE = 9;
    Engine.SCALEMODE_FLOOR = 1;
    Engine.SCALEMODE_NEAREST = 2;
    Engine.SCALEMODE_CEILING = 3;
  }
});

// node_modules/@babylonjs/core/Offline/database.js
var Database;
var init_database = __esm({
  "node_modules/@babylonjs/core/Offline/database.js"() {
    "use strict";
    init_tools();
    init_logger();
    init_tga();
    init_webRequest();
    init_abstractEngine();
    AbstractEngine.OfflineProviderFactory = (urlToScene, callbackManifestChecked, disableManifestCheck = false) => {
      return new Database(urlToScene, callbackManifestChecked, disableManifestCheck);
    };
    Database = class _Database {
      /**
       * Gets a boolean indicating if scene must be saved in the database
       */
      get enableSceneOffline() {
        return this._enableSceneOffline;
      }
      /**
       * Gets a boolean indicating if textures must be saved in the database
       */
      get enableTexturesOffline() {
        return this._enableTexturesOffline;
      }
      /**
       * Creates a new Database
       * @param urlToScene defines the url to load the scene
       * @param callbackManifestChecked defines the callback to use when manifest is checked
       * @param disableManifestCheck defines a boolean indicating that we want to skip the manifest validation (it will be considered validated and up to date)
       */
      constructor(urlToScene, callbackManifestChecked, disableManifestCheck = false) {
        this._idbFactory = typeof indexedDB !== "undefined" ? indexedDB : void 0;
        this._currentSceneUrl = _Database._ReturnFullUrlLocation(urlToScene);
        this._db = null;
        this._enableSceneOffline = false;
        this._enableTexturesOffline = false;
        this._manifestVersionFound = 0;
        this._mustUpdateRessources = false;
        this._hasReachedQuota = false;
        if (!_Database.IDBStorageEnabled) {
          callbackManifestChecked(true);
        } else {
          if (disableManifestCheck) {
            this._enableSceneOffline = true;
            this._enableTexturesOffline = true;
            this._manifestVersionFound = 1;
            Tools.SetImmediate(() => {
              callbackManifestChecked(true);
            });
          } else {
            this._checkManifestFile(callbackManifestChecked);
          }
        }
      }
      _checkManifestFile(callbackManifestChecked) {
        const noManifestFile = () => {
          this._enableSceneOffline = false;
          this._enableTexturesOffline = false;
          callbackManifestChecked(false);
        };
        const createManifestURL = () => {
          try {
            if (typeof URL === "function" && this._currentSceneUrl.indexOf("http") === 0) {
              const url = new URL(this._currentSceneUrl);
              url.pathname += ".manifest";
              return url.toString();
            }
          } catch (e) {
          }
          return `${this._currentSceneUrl}.manifest`;
        };
        let timeStampUsed = false;
        let manifestURL = createManifestURL();
        const xhr = new WebRequest();
        if (navigator.onLine) {
          timeStampUsed = true;
          manifestURL = manifestURL + (manifestURL.match(/\?/) == null ? "?" : "&") + Date.now();
        }
        xhr.open("GET", manifestURL);
        xhr.addEventListener("load", () => {
          if (xhr.status === 200 || _Database._ValidateXHRData(xhr, 1)) {
            try {
              const manifestFile = JSON.parse(xhr.response);
              this._enableSceneOffline = manifestFile.enableSceneOffline;
              this._enableTexturesOffline = manifestFile.enableTexturesOffline && _Database._IsUASupportingBlobStorage;
              if (manifestFile.version && !isNaN(parseInt(manifestFile.version))) {
                this._manifestVersionFound = manifestFile.version;
              }
              callbackManifestChecked(true);
            } catch (ex) {
              noManifestFile();
            }
          } else {
            noManifestFile();
          }
        }, false);
        xhr.addEventListener("error", () => {
          if (timeStampUsed) {
            timeStampUsed = false;
            const retryManifestURL = createManifestURL();
            xhr.open("GET", retryManifestURL);
            xhr.send();
          } else {
            noManifestFile();
          }
        }, false);
        try {
          xhr.send();
        } catch (ex) {
          Logger.Error("Error on XHR send request.");
          callbackManifestChecked(false);
        }
      }
      /**
       * Open the database and make it available
       * @param successCallback defines the callback to call on success
       * @param errorCallback defines the callback to call on error
       */
      open(successCallback, errorCallback) {
        const handleError = () => {
          this._isSupported = false;
          if (errorCallback) {
            errorCallback();
          }
        };
        if (!this._idbFactory || !(this._enableSceneOffline || this._enableTexturesOffline)) {
          this._isSupported = false;
          if (errorCallback) {
            errorCallback();
          }
        } else {
          if (!this._db) {
            this._hasReachedQuota = false;
            this._isSupported = true;
            const request = this._idbFactory.open("babylonjs", 1);
            request.onerror = () => {
              handleError();
            };
            request.onblocked = () => {
              Logger.Error("IDB request blocked. Please reload the page.");
              handleError();
            };
            request.onsuccess = () => {
              this._db = request.result;
              successCallback();
            };
            request.onupgradeneeded = (event) => {
              this._db = event.target.result;
              if (this._db) {
                try {
                  this._db.createObjectStore("scenes", { keyPath: "sceneUrl" });
                  this._db.createObjectStore("versions", { keyPath: "sceneUrl" });
                  this._db.createObjectStore("textures", { keyPath: "textureUrl" });
                } catch (ex) {
                  Logger.Error("Error while creating object stores. Exception: " + ex.message);
                  handleError();
                }
              }
            };
          } else {
            if (successCallback) {
              successCallback();
            }
          }
        }
      }
      /**
       * Loads an image from the database
       * @param url defines the url to load from
       * @param image defines the target DOM image
       */
      loadImage(url, image) {
        const completeURL = _Database._ReturnFullUrlLocation(url);
        const saveAndLoadImage = () => {
          if (!this._hasReachedQuota && this._db !== null) {
            this._saveImageIntoDBAsync(completeURL, image);
          } else {
            image.src = url;
          }
        };
        if (!this._mustUpdateRessources) {
          this._loadImageFromDBAsync(completeURL, image, saveAndLoadImage);
        } else {
          saveAndLoadImage();
        }
      }
      _loadImageFromDBAsync(url, image, notInDBCallback) {
        if (this._isSupported && this._db !== null) {
          let texture;
          const transaction = this._db.transaction(["textures"]);
          transaction.onabort = () => {
            image.src = url;
          };
          transaction.oncomplete = () => {
            let blobTextureURL;
            if (texture && typeof URL === "function") {
              blobTextureURL = URL.createObjectURL(texture.data);
              image.onerror = () => {
                Logger.Error("Error loading image from blob URL: " + blobTextureURL + " switching back to web url: " + url);
                image.src = url;
              };
              image.src = blobTextureURL;
            } else {
              notInDBCallback();
            }
          };
          const getRequest = transaction.objectStore("textures").get(url);
          getRequest.onsuccess = (event) => {
            texture = event.target.result;
          };
          getRequest.onerror = () => {
            Logger.Error("Error loading texture " + url + " from DB.");
            image.src = url;
          };
        } else {
          Logger.Error("Error: IndexedDB not supported by your browser or BabylonJS Database is not open.");
          image.src = url;
        }
      }
      _saveImageIntoDBAsync(url, image) {
        let blob;
        if (this._isSupported) {
          const generateBlobUrl = () => {
            let blobTextureURL;
            if (blob && typeof URL === "function") {
              try {
                blobTextureURL = URL.createObjectURL(blob);
              } catch (ex) {
                blobTextureURL = URL.createObjectURL(blob);
              }
            }
            if (blobTextureURL) {
              image.src = blobTextureURL;
            }
          };
          if (_Database._IsUASupportingBlobStorage) {
            const xhr = new WebRequest();
            xhr.open("GET", url);
            xhr.responseType = "blob";
            xhr.addEventListener("load", () => {
              if (xhr.status === 200 && this._db) {
                blob = xhr.response;
                const transaction = this._db.transaction(["textures"], "readwrite");
                transaction.onabort = (event) => {
                  try {
                    const srcElement = event.target;
                    const error = srcElement.error;
                    if (error && error.name === "QuotaExceededError") {
                      this._hasReachedQuota = true;
                    }
                  } catch (ex) {
                  }
                  generateBlobUrl();
                };
                transaction.oncomplete = () => {
                  generateBlobUrl();
                };
                const newTexture = { textureUrl: url, data: blob };
                try {
                  const addRequest = transaction.objectStore("textures").put(newTexture);
                  addRequest.onsuccess = () => {
                  };
                  addRequest.onerror = () => {
                    generateBlobUrl();
                  };
                } catch (ex) {
                  if (ex.code === 25) {
                    _Database._IsUASupportingBlobStorage = false;
                    this._enableTexturesOffline = false;
                  }
                  image.src = url;
                }
              } else {
                image.src = url;
              }
            }, false);
            xhr.addEventListener("error", () => {
              Logger.Error("Error in XHR request in BABYLON.Database.");
              image.src = url;
            }, false);
            xhr.send();
          } else {
            image.src = url;
          }
        } else {
          Logger.Error("Error: IndexedDB not supported by your browser or Babylon.js database is not open.");
          image.src = url;
        }
      }
      _checkVersionFromDB(url, versionLoaded) {
        const updateVersion = () => {
          this._saveVersionIntoDBAsync(url, versionLoaded);
        };
        this._loadVersionFromDBAsync(url, versionLoaded, updateVersion);
      }
      _loadVersionFromDBAsync(url, callback, updateInDBCallback) {
        if (this._isSupported && this._db) {
          let version;
          try {
            const transaction = this._db.transaction(["versions"]);
            transaction.oncomplete = () => {
              if (version) {
                if (this._manifestVersionFound !== version.data) {
                  this._mustUpdateRessources = true;
                  updateInDBCallback();
                } else {
                  callback(version.data);
                }
              } else {
                this._mustUpdateRessources = true;
                updateInDBCallback();
              }
            };
            transaction.onabort = () => {
              callback(-1);
            };
            const getRequest = transaction.objectStore("versions").get(url);
            getRequest.onsuccess = (event) => {
              version = event.target.result;
            };
            getRequest.onerror = () => {
              Logger.Error("Error loading version for scene " + url + " from DB.");
              callback(-1);
            };
          } catch (ex) {
            Logger.Error("Error while accessing 'versions' object store (READ OP). Exception: " + ex.message);
            callback(-1);
          }
        } else {
          Logger.Error("Error: IndexedDB not supported by your browser or Babylon.js database is not open.");
          callback(-1);
        }
      }
      _saveVersionIntoDBAsync(url, callback) {
        if (this._isSupported && !this._hasReachedQuota && this._db) {
          try {
            const transaction = this._db.transaction(["versions"], "readwrite");
            transaction.onabort = (event) => {
              try {
                const error = event.target["error"];
                if (error && error.name === "QuotaExceededError") {
                  this._hasReachedQuota = true;
                }
              } catch (ex) {
              }
              callback(-1);
            };
            transaction.oncomplete = () => {
              callback(this._manifestVersionFound);
            };
            const newVersion = { sceneUrl: url, data: this._manifestVersionFound };
            const addRequest = transaction.objectStore("versions").put(newVersion);
            addRequest.onsuccess = () => {
            };
            addRequest.onerror = () => {
              Logger.Error("Error in DB add version request in BABYLON.Database.");
            };
          } catch (ex) {
            Logger.Error("Error while accessing 'versions' object store (WRITE OP). Exception: " + ex.message);
            callback(-1);
          }
        } else {
          callback(-1);
        }
      }
      /**
       * Loads a file from database
       * @param url defines the URL to load from
       * @param sceneLoaded defines a callback to call on success
       * @param progressCallBack defines a callback to call when progress changed
       * @param errorCallback defines a callback to call on error
       * @param useArrayBuffer defines a boolean to use array buffer instead of text string
       */
      loadFile(url, sceneLoaded, progressCallBack, errorCallback, useArrayBuffer) {
        const completeUrl = _Database._ReturnFullUrlLocation(url);
        const saveAndLoadFile = () => {
          this._saveFileAsync(completeUrl, sceneLoaded, progressCallBack, useArrayBuffer, errorCallback);
        };
        this._checkVersionFromDB(completeUrl, (version) => {
          if (version !== -1) {
            if (!this._mustUpdateRessources) {
              this._loadFileAsync(completeUrl, sceneLoaded, saveAndLoadFile, progressCallBack);
            } else {
              this._saveFileAsync(completeUrl, sceneLoaded, progressCallBack, useArrayBuffer, errorCallback);
            }
          } else {
            if (errorCallback) {
              errorCallback();
            }
          }
        });
      }
      _loadFileAsync(url, callback, notInDBCallback, progressCallBack) {
        if (this._isSupported && this._db) {
          let targetStore;
          if (url.indexOf(".babylon") !== -1) {
            targetStore = "scenes";
          } else {
            targetStore = "textures";
          }
          let file;
          const transaction = this._db.transaction([targetStore]);
          transaction.oncomplete = () => {
            var _a;
            if (file) {
              if (progressCallBack) {
                const numberToLoad = ((_a = file.data) == null ? void 0 : _a.byteLength) || 0;
                progressCallBack({
                  total: numberToLoad,
                  loaded: numberToLoad,
                  lengthComputable: true
                });
              }
              callback(file.data);
            } else {
              notInDBCallback();
            }
          };
          transaction.onabort = () => {
            notInDBCallback();
          };
          const getRequest = transaction.objectStore(targetStore).get(url);
          getRequest.onsuccess = (event) => {
            file = event.target.result;
          };
          getRequest.onerror = () => {
            Logger.Error("Error loading file " + url + " from DB.");
            notInDBCallback();
          };
        } else {
          Logger.Error("Error: IndexedDB not supported by your browser or BabylonJS Database is not open.");
          callback();
        }
      }
      _saveFileAsync(url, callback, progressCallback, useArrayBuffer, errorCallback) {
        if (this._isSupported) {
          let targetStore;
          if (url.indexOf(".babylon") !== -1) {
            targetStore = "scenes";
          } else {
            targetStore = "textures";
          }
          const xhr = new WebRequest();
          let fileData;
          xhr.open("GET", url + (url.match(/\?/) == null ? "?" : "&") + Date.now());
          if (useArrayBuffer) {
            xhr.responseType = "arraybuffer";
          }
          if (progressCallback) {
            xhr.onprogress = progressCallback;
          }
          xhr.addEventListener("load", () => {
            if (xhr.status === 200 || xhr.status < 400 && _Database._ValidateXHRData(xhr, !useArrayBuffer ? 1 : 6)) {
              fileData = !useArrayBuffer ? xhr.responseText : xhr.response;
              if (!this._hasReachedQuota && this._db) {
                const transaction = this._db.transaction([targetStore], "readwrite");
                transaction.onabort = (event) => {
                  try {
                    const error = event.target["error"];
                    if (error && error.name === "QuotaExceededError") {
                      this._hasReachedQuota = true;
                    }
                  } catch (ex) {
                  }
                  callback(fileData);
                };
                transaction.oncomplete = () => {
                  callback(fileData);
                };
                let newFile;
                if (targetStore === "scenes") {
                  newFile = { sceneUrl: url, data: fileData, version: this._manifestVersionFound };
                } else {
                  newFile = { textureUrl: url, data: fileData };
                }
                try {
                  const addRequest = transaction.objectStore(targetStore).put(newFile);
                  addRequest.onsuccess = () => {
                  };
                  addRequest.onerror = () => {
                    Logger.Error("Error in DB add file request in BABYLON.Database.");
                  };
                } catch (ex) {
                  callback(fileData);
                }
              } else {
                callback(fileData);
              }
            } else {
              if (xhr.status >= 400 && errorCallback) {
                errorCallback(xhr);
              } else {
                callback();
              }
            }
          }, false);
          xhr.addEventListener("error", () => {
            Logger.Error("error on XHR request.");
            errorCallback && errorCallback();
          }, false);
          xhr.send();
        } else {
          Logger.Error("Error: IndexedDB not supported by your browser or Babylon.js database is not open.");
          errorCallback && errorCallback();
        }
      }
      /**
       * Validates if xhr data is correct
       * @param xhr defines the request to validate
       * @param dataType defines the expected data type
       * @returns true if data is correct
       */
      static _ValidateXHRData(xhr, dataType = 7) {
        try {
          if (dataType & 1) {
            if (xhr.responseText && xhr.responseText.length > 0) {
              return true;
            } else if (dataType === 1) {
              return false;
            }
          }
          if (dataType & 2) {
            const tgaHeader = GetTGAHeader(xhr.response);
            if (tgaHeader.width && tgaHeader.height && tgaHeader.width > 0 && tgaHeader.height > 0) {
              return true;
            } else if (dataType === 2) {
              return false;
            }
          }
          if (dataType & 4) {
            const ddsHeader = new Uint8Array(xhr.response, 0, 3);
            if (ddsHeader[0] === 68 && ddsHeader[1] === 68 && ddsHeader[2] === 83) {
              return true;
            } else {
              return false;
            }
          }
        } catch (e) {
        }
        return false;
      }
    };
    Database._IsUASupportingBlobStorage = true;
    Database.IDBStorageEnabled = false;
    Database._ParseURL = (url) => {
      const a = document.createElement("a");
      a.href = url;
      const urlWithoutHash = url.substring(0, url.lastIndexOf("#"));
      const fileName = url.substring(urlWithoutHash.lastIndexOf("/") + 1, url.length);
      const absLocation = url.substring(0, url.indexOf(fileName, 0));
      return absLocation;
    };
    Database._ReturnFullUrlLocation = (url) => {
      if (url.indexOf("http:/") === -1 && url.indexOf("https:/") === -1 && typeof window !== "undefined") {
        return Database._ParseURL(window.location.href) + url;
      } else {
        return url;
      }
    };
  }
});

// node_modules/@babylonjs/core/Offline/IOfflineProvider.js
var init_IOfflineProvider = __esm({
  "node_modules/@babylonjs/core/Offline/IOfflineProvider.js"() {
    "use strict";
  }
});

// node_modules/@babylonjs/core/Offline/index.js
var init_Offline = __esm({
  "node_modules/@babylonjs/core/Offline/index.js"() {
    "use strict";
    init_database();
    init_IOfflineProvider();
  }
});

export {
  PerformanceMonitor,
  RollingAverage,
  init_performanceMonitor,
  init_engine_alpha,
  init_engine_dynamicBuffer,
  init_abstractEngine_loadingScreen,
  init_abstractEngine_dom,
  init_abstractEngine_alpha,
  init_abstractEngine_states,
  init_abstractEngine_renderPass,
  _CommonInit,
  _CommonDispose,
  GetFontOffset,
  CreateImageBitmapFromSource,
  ResizeImageBitmap,
  RequestFullscreen,
  ExitFullscreen,
  RequestPointerlock,
  ExitPointerlock,
  init_engine_common,
  AudioEngine,
  init_audioEngine,
  Engine,
  init_engine,
  Database,
  init_Offline
};
